import { useQuery } from '@tanstack/react-query';
import { awaitJSON, S3SignedUrlFetch } from '@src/utils/fetch-utils';

interface WeekData {
  name: string;
  week: string;
  colHeaders: string[];
  rowHeaders: string[];
  spend: number[][];
  spots: number[][];
}

export interface CostPersRawData {
  weeks: WeekData[];
}

export type CreativesRawData = Record<string, number>;

const getS3Data = async <T>(filename: string, selectedCompetitor: string): Promise<T> => {
  const res = await S3SignedUrlFetch(
    `bpm-ml-data/competitive/latest/${selectedCompetitor}_${filename}.json.gz`
  );
  try {
    return await awaitJSON<T>(res);
  } catch (e) {
    if ((e.message || '').includes('NoSuchKey')) {
      throw new Error('No data found', { cause: e });
    }
    throw e;
  }
};

export const useCompetitiveMetricsData = (selectedCompetitor: string | undefined) =>
  useQuery({
    queryKey: ['competitiveMetrics', selectedCompetitor],
    enabled: Boolean(selectedCompetitor),
    // Missing competitor data is common; the page shows it in an EmptyState instead of reporting it.
    meta: { suppressGlobalError: true },
    retry: false,
    queryFn: async () => {
      if (!selectedCompetitor) {
        throw new Error('No competitor selected');
      }
      let costPersRawData: CostPersRawData;
      try {
        costPersRawData = await getS3Data<CostPersRawData>('rawCostPers', selectedCompetitor);
      } catch (e) {
        throw new Error(
          `Failed to load costPers data for ${selectedCompetitor}. Error: ${e.message}`,
          { cause: e }
        );
      }
      try {
        const creativesRawData = await getS3Data<CreativesRawData>('creatives', selectedCompetitor);
        return { costPersRawData, creativesRawData };
      } catch (e) {
        throw new Error(
          `Failed to load creatives data for ${selectedCompetitor}. Error: ${e.message}`,
          { cause: e }
        );
      }
    }
  });
